import { z } from 'zod'
import { IngestEnvelopeSchema, PatientHeaderSchema, SchemaRefSchema } from './envelope'

/**
 * Data sets carry the **name** of the tablet that uploaded them, captured at
 * ingest time. They are intentionally *not* joined to the tablets table by
 * id: tablet hardware is considered disposable, so when a physical device
 * fails and is replaced by another tablet registered under the same name,
 * previously-ingested data sets continue to read correctly. Revoked tablets
 * are deleted from the database outright; their uploads remain identifiable
 * via this string.
 */
export const DataSetSchema = z.object({
  id: z.string().uuid(),
  tabletName: z.string().min(1).max(120),
  schema: SchemaRefSchema,
  capturedAt: z.string().datetime({ offset: true }),
  receivedAt: z.string().datetime({ offset: true }),
  patient: PatientHeaderSchema,
  data: z.record(z.unknown()),
})

export const DataSetSummarySchema = DataSetSchema.omit({ data: true })

export const DataSetQuerySchema = z.object({
  q: z.string().min(1).max(200).optional(),
  tabletName: z.string().min(1).max(120).optional(),
  schema: SchemaRefSchema.optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  limit: z.coerce.number().int().positive().max(200).default(50),
  offset: z.coerce.number().int().nonnegative().default(0),
})

export const DataSetListResponseSchema = z.object({
  items: z.array(DataSetSummarySchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
})

/**
 * Body for the session-authenticated ingest path: same shape as the tablet
 * ingest envelope but without `tabletUid`. The server stamps the row's
 * `tabletName` from the session user's email so re-saves of the same QR by
 * the same user dedupe naturally via the existing content-hash unique
 * constraint.
 */
export const ManualIngestRequestSchema = IngestEnvelopeSchema.omit({ tabletUid: true })

/**
 * Pre-save existence check for an envelope — same canonical content as
 * `ManualIngestRequestSchema` minus `capturedAt`, since the content hash
 * deliberately excludes capture time.
 */
export const DataSetLookupRequestSchema = ManualIngestRequestSchema.omit({ capturedAt: true })

export const DataSetIngestResultSchema = z.object({
  id: z.string().uuid(),
  alreadyExisted: z.boolean(),
})

export const DataSetLookupResultSchema = z.object({
  id: z.string().uuid().nullable(),
})

export type DataSet = z.infer<typeof DataSetSchema>
export type DataSetSummary = z.infer<typeof DataSetSummarySchema>
export type DataSetQuery = z.infer<typeof DataSetQuerySchema>
export type DataSetListResponse = z.infer<typeof DataSetListResponseSchema>
export type ManualIngestRequest = z.infer<typeof ManualIngestRequestSchema>
export type DataSetLookupRequest = z.infer<typeof DataSetLookupRequestSchema>
export type DataSetIngestResult = z.infer<typeof DataSetIngestResultSchema>
export type DataSetLookupResult = z.infer<typeof DataSetLookupResultSchema>
